import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Building2 } from "lucide-react";

interface Department {
  id: string;
  name: string;
  ai_employee_name: string | null;
  created_at: string;
  updated_at: string;
}

const DepartmentManagement = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);

  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [name, setName] = useState("");
  const [aiEmployeeName, setAiEmployeeName] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingDept, setDeletingDept] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDepartments = async () => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("department")
      .select("*")
      .eq("client_id", profile.client_id)
      .order("name");
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setDepartments((data as unknown as Department[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDepartments();
  }, [profile?.client_id]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setAiEmployeeName("");
    setDialogOpen(true);
  };

  const openEdit = (dept: Department) => {
    setEditing(dept);
    setName(dept.name);
    setAiEmployeeName(dept.ai_employee_name || "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !profile?.client_id) return;
    setSaving(true);
    try {
      if (editing) {
        const { error } = await supabase
          .from("department")
          .update({
            name: name.trim(),
            ai_employee_name: aiEmployeeName.trim() || null,
          })
          .eq("id", editing.id);
        if (error) throw new Error(error.message);
        toast({ title: "Updated", description: `Department "${name.trim()}" updated.` });
      } else {
        const { error } = await supabase
          .from("department")
          .insert({
            name: name.trim(),
            ai_employee_name: aiEmployeeName.trim() || null,
            client_id: profile.client_id,
          });
        if (error) throw new Error(error.message);
        toast({ title: "Created", description: `Department "${name.trim()}" created.` });
      }
      setDialogOpen(false);
      await fetchDepartments();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deletingDept) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("department").delete().eq("id", deletingDept.id);
      if (error) throw new Error(error.message);
      toast({ title: "Deleted", description: `Department "${deletingDept.name}" deleted.` });
      setDeleteConfirmOpen(false);
      setDeletingDept(null);
      await fetchDepartments();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="sticky top-0 z-10 bg-background border-b px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-2xl font-semibold">Departments</h1>
          <p className="text-sm text-muted-foreground">Manage departments for your organization.</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Add Department
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {departments.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="text-lg mb-1">No departments yet</CardTitle>
            <CardDescription>Create your first department to organize workflows and employees.</CardDescription>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department Name</TableHead>
                  <TableHead>AI Employee Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((dept) => (
                  <TableRow key={dept.id}>
                    <TableCell className="font-medium">{dept.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {dept.ai_employee_name || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(dept.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(dept)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => { setDeletingDept(dept); setDeleteConfirmOpen(true); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Department" : "Add Department"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update the department details." : "Create a new department for your organization."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Department Name</Label>
              <Input
                placeholder="e.g. Sales, Engineering, Marketing"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label>AI Employee Name</Label>
              <Input
                placeholder="e.g. Sales Assistant, Support Bot"
                value={aiEmployeeName}
                onChange={(e) => setAiEmployeeName(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                The name of the AI employee assigned to this department.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!name.trim() || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Department</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingDept?.name}"? Workflows and employees in this department will be unassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentManagement;
